import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import image1 from '../../../../assets/login/image1.png';
import image2 from '../../../../assets/login/image2.png';
import image3 from '../../../../assets/login/image3.png';
import image4 from '../../../../assets/login/image4.png';
import image5 from '../../../../assets/login/image5.png';

import image6 from '../../../../assets/login/image6.png';
import image9 from '../../../../assets/login/image9.png';
import image10 from '../../../../assets/login/image10.png';

import image12 from '../../../../assets/login/image12.png';
import image13 from '../../../../assets/login/image13.png';
import image14 from '../../../../assets/login/image14.png';

import image17 from '../../../../assets/login/image17.png';
import image18 from '../../../../assets/login/image18.png';
import image19 from '../../../../assets/login/image19.png';
import image20 from '../../../../assets/login/image20.png';


// TypeScript interfaces
interface ImageConfig {
  className: string;
  height: string;
}

interface ImageItem {
  src: string;
  className: string;
  height: string;
}

interface StatsBlock {
  type: 'stats';
  value: string;
  text: string;
  color: string;
  className: string;
  height: string;
}

type ColumnItem = ImageItem | StatsBlock;

// Define different image sizes and positions
const imageConfigs: ImageConfig[] = [
  {className: 'col-span-1 row-span-2', height: 'h-80'}, // Tall
  {className: 'col-span-2 row-span-1', height: 'h-40'}, // Wide
  {className: 'col-span-1 row-span-1', height: 'h-40'}, // Standard
  {className: 'col-span-1 row-span-1', height: 'h-40'}, // Standard
  {className: 'col-span-2 row-span-1', height: 'h-40'}, // Wide
  {className: 'col-span-1 row-span-2', height: 'h-80'}, // Tall
  {className: 'col-span-1 row-span-1', height: 'h-40'}, // Standard
  {className: 'col-span-1 row-span-1', height: 'h-40'}, // Standard
];

// Create columns with image configurations
const createColumnImages = (images: string[]): ImageItem[] => {
  return images.map((img, index) => ({
    src: img,
    ...imageConfigs[index % imageConfigs.length],
  }));
};

const col1 = createColumnImages([image1, image2, image3, image4, image5]);
const col2 = createColumnImages([image6, image9,image10]);

const col3 = createColumnImages([image12, image13, image14]);
const col4 = createColumnImages([
  image17,
  image18,
  image19,
  image20,
]);

// Add some special stats blocks
const statsBlocks: StatsBlock[] = [
  {
    type: 'stats',
    value: '90%',
    text: 'Faster Processing Time',
    color: 'bg-pink-500',
    className: 'col-span-1 row-span-2',
    height: 'h-60',
  },
  {
    type: 'stats',
    value: '95%',
    text: 'Error Reduction Rate',
    color: 'bg-indigo-600',
    className: 'col-span-2 row-span-2',
    height: 'h-60',
  },
  {
    type: 'stats',
    value: '10x',
    text: 'Scalability',
    color: 'bg-green-600',
    className: 'col-span-2 row-span-2',
    height: 'h-60',
  },
  {
    type: 'stats',
    value: '99%',
    text: 'Accuracy & Seamless integration',
    color: 'bg-yellow-600',
    className: 'col-span-2 row-span-2',
    height: 'h-60',
  },
];

// Insert stats blocks at specific positions in the columns
const columns: ColumnItem[][] = [
  [...col1],
  [...col2.slice(0, 2), statsBlocks[2], ...col2.slice(2), statsBlocks[0]],
  [...col3.slice(0, 2), statsBlocks[1], ...col3.slice(2), statsBlocks[3]],
  [...col4],
];

const SCROLL_DURATION = 40; // Slower scrolling for better visibility

const InfiniteScrollGallery: React.FC = () => {
  const [paused, setPaused] = useState<boolean>(false);
  const columnRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [columnHeights, setColumnHeights] = useState<number[]>([]);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Measure actual heights of each column once mounted
  useEffect(() => {
    const measureHeights = () => {
      const heights = columnRefs.current.map(ref => {
        if (ref) {
          return ref.scrollHeight / 2;
        }
        return 0;
      });

      setColumnHeights(heights);
      setIsInitialized(true);
    };

    // Wait for a frame to ensure DOM is ready
    requestAnimationFrame(() => {
      measureHeights();
    });
  }, []);

  return (
    <main className="flex h-screen w-full">
      <div
        className="w-full bg-gray-100 p-2 overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}>
        <div className="grid grid-cols-4 gap-1 w-full h-full bg-[#000000] overflow-hidden rounded-[20px] !flex">
          {columns.map((col, colIndex) => {
            const isEven = colIndex % 2 === 0;
            const columnHeight = isInitialized ? columnHeights[colIndex] : 0;

            return (
              <div
                key={colIndex}
                className={`relative overflow-hidden h-screen ${
                  colIndex === 0 || colIndex === columns.length - 1
                    ? 'w-3/4'
                    : 'w-full'
                }`}>
                <motion.div
                  ref={(el) => {columnRefs.current[colIndex] = el}}
                  className="flex flex-col space-y-2 absolute w-full"
                  initial={{
                    y: isEven ? 0 : -columnHeight,
                  }}
                  animate={
                    isInitialized
                      ? {
                          y: paused
                            ? isEven
                              ? 0
                              : -columnHeight
                            : isEven
                            ? [0, -columnHeight]
                            : [-columnHeight, 0],
                        }
                      : {}
                  }
                  transition={{
                    repeat: Infinity,
                    repeatType: 'loop',
                    duration: SCROLL_DURATION,
                    ease: 'linear',
                  }}>
                  {/* Two copies of the content for seamless loop */}
                  {[...col, ...col].map((item, itemIndex) => (
                    <div
                      key={`item-${colIndex}-${itemIndex}`}
                      className={`relative ${item.className} ${
                        item.height
                      } rounded-lg overflow-hidden shadow-md ${
                        colIndex === 0 || colIndex === columns.length - 1
                          ? 'h-80'
                          : ''
                      }`}>
                      {'type' in item && item.type === 'stats' ? (
                        // Stats block
                        <div
                          className={`w-full h-full ${item.color} text-white flex flex-col items-center justify-center p-4`}>
                          <div className="text-6xl font-bold">{item.value}</div>
                          <div className="text-center mt-2">{item.text}</div>
                        </div>
                      ) : (
                        // Image block
                        <img
                          src={'src' in item ? item.src : ''}
                          alt={`Image ${itemIndex}`}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                  ))}
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </main>
  );
};

export default InfiniteScrollGallery;
